import {
	AvailableBindersAndWarrantiesForConstructionSite,
	BinderFailuresByWarrantyType,
	BrokerCommunication,
	ConstructionSiteFullDetails,
	ConstructionSiteList,
	ConstructionSiteOffer,
	OfferPricingOutput,
	OffersWarrantyType,
	PaginatedResponse,
	Task,
	TaskType,
} from "@dune/types";
import { formatPricing } from "@dune/utils";
import {
	keepPreviousData,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { privateAxios } from "../axios";
import { constructionSitesQueryKeys } from "../query-keys/constructionSites.query-keys";

const getClients = async (searchInput: string, excludeSyndics = false) => {
	const params = new URLSearchParams();
	if (excludeSyndics) params.append("excludeSyndics", "true");

	const { data } = await privateAxios.get(
		`/constructionSites/clients/${searchInput}${params.toString() ? `?${params.toString()}` : ""}`,
	);
	return data;
};

export const useGetClients = (searchInput: string, excludeSyndics = false) => {
	return useQuery({
		queryKey: constructionSitesQueryKeys.clientSearch(
			searchInput,
			excludeSyndics,
		),
		queryFn: () => getClients(searchInput, excludeSyndics),
		enabled: !!searchInput,
	});
};

const getSyndics = async (searchInput: string) => {
	const { data } = await privateAxios.get(
		`/constructionSites/syndics/${searchInput}`,
	);
	return data;
};

export const useGetSyndics = (searchInput: string) => {
	return useQuery({
		queryKey: constructionSitesQueryKeys.syndicSearch(searchInput),
		queryFn: () => getSyndics(searchInput),
		enabled: !!searchInput,
	});
};

const getConstructionSiteSyndicInfo = async (id: string) => {
	const { data } = await privateAxios.get(
		`/constructionSites/${id}/syndicInfo`,
	);
	return data;
};

export const useGetConstructionSiteSyndicInfo = (id: string) => {
	return useQuery({
		queryKey: constructionSitesQueryKeys.syndicInfo(id),
		queryFn: () => getConstructionSiteSyndicInfo(id),
		enabled: !!id,
	});
};

const getConstructionSites = async (
	search: string,
	page?: number,
	limit?: number,
) => {
	const searchParams = new URLSearchParams();
	if (search) searchParams.append("search", search);
	if (page) searchParams.append("page", String(page));
	if (limit) searchParams.append("limit", String(limit));

	const { data } = await privateAxios.get<
		PaginatedResponse<ConstructionSiteList>
	>(`/constructionSites?${searchParams.toString()}`);
	return data;
};

export const useGetConstructionSites = (
	search: string,
	page?: number,
	limit?: number,
) => {
	return useQuery({
		queryKey: constructionSitesQueryKeys.list(search, page, limit),
		queryFn: () => getConstructionSites(search, page, limit),
		placeholderData: keepPreviousData,
	});
};

const getConstructionSiteBindersWarranties = async (id: number) => {
	const { data } = await privateAxios.get<{
		availableBindersAndWarranties: AvailableBindersAndWarrantiesForConstructionSite[];
		allFailures: BinderFailuresByWarrantyType;
	}>(`/binders/constructionSites/${id}`);
	return data;
};

export const useGetConstructionSiteBindersWarranties = (id: number) => {
	return useQuery({
		queryKey: constructionSitesQueryKeys.binders(id),
		queryFn: async () => getConstructionSiteBindersWarranties(id),
	});
};

const getConstructionSiteDetails = async (id: string) => {
	const { data } = await privateAxios.get<ConstructionSiteFullDetails>(
		`/constructionSites/${id}`,
	);
	return data;
};

export const useGetConstructionSiteDetails = (id: string) => {
	return useQuery({
		queryKey: constructionSitesQueryKeys.detail(id),
		queryFn: () => getConstructionSiteDetails(id),
		enabled: !!id,
	});
};

const getConstructionSiteCommunications = async (
	constructionSiteId: string,
	options?: {
		page?: number;
		limit?: number;
		sortBy?: string;
		sortOrder?: "asc" | "desc";
	},
) => {
	const { data } = await privateAxios.get<
		PaginatedResponse<BrokerCommunication>
	>(`/constructionSites/${constructionSiteId}/communications`, {
		params: options,
	});
	return data;
};

export const useGetConstructionSiteCommunications = (
	constructionSiteId: string,
	options?: {
		page?: number;
		limit?: number;
		sortBy?: string;
		sortOrder?: "asc" | "desc";
	},
) => {
	return useQuery({
		queryKey: constructionSitesQueryKeys.communications(
			constructionSiteId,
			options,
		),
		queryFn: () =>
			getConstructionSiteCommunications(constructionSiteId, options),
		enabled: !!constructionSiteId,
	});
};

const getConstructionSiteOffers = async (
	constructionSiteId: string,
): Promise<ConstructionSiteOffer[]> => {
	const { data } = await privateAxios.get<ConstructionSiteOffer[]>(
		`/constructionSites/${constructionSiteId}/offers`,
	);
	return data;
};

export const useGetConstructionSiteOffers = (constructionSiteId: string) => {
	return useQuery({
		queryKey: constructionSitesQueryKeys.offers(constructionSiteId),
		queryFn: () => getConstructionSiteOffers(constructionSiteId),
		enabled: !!constructionSiteId,
	});
};

const evaluateInitialPricing = async <T extends OffersWarrantyType>(
	constructionSiteId: string,
	warrantyType: T,
	binderAmendmentId: number,
	body: {
		existingWarrantyRate: number;
		isNonPerformingContractor: boolean;
		brokerFees: number;
		returnMinimalOutput?: boolean;
	},
): Promise<OfferPricingOutput<T, "INITIAL">> => {
	const params = new URLSearchParams({
		warrantyType,
		binderAmendmentId: binderAmendmentId.toString(),
		constructionSiteId,
	});

	const { data } = await privateAxios.post(
		`/pricing-files/evaluate-initial-pricing?${params.toString()}`,
		body,
	);

	return data;
};

export const useEvaluateInitialPricing = (
	constructionSiteId: string,
	warrantyType: OffersWarrantyType | null,
	binderAmendmentId: number,
	body: {
		existingWarrantyRate: number;
		isNonPerformingContractor: boolean;
		brokerFees: number;
		returnMinimalOutput?: boolean;
	},
	format: (input: number) => string = formatPricing,
	enabled = true,
) => {
	return useQuery({
		queryKey: [
			"evaluateInitialPricing",
			constructionSiteId,
			warrantyType,
			binderAmendmentId,
			body.existingWarrantyRate,
			body.isNonPerformingContractor,
			body.brokerFees,
		],
		queryFn: async () => {
			if (warrantyType === null) {
				throw new Error("Warranty type is required");
			}

			const pricingData = await evaluateInitialPricing(
				constructionSiteId,
				warrantyType,
				binderAmendmentId,
				body,
			);

			return {
				totalGrossPremium: format(pricingData.totalClientPrimeTTC),
				brokerCommission: format(pricingData.brokerCommission),
				brokerRemuneration: format(pricingData.brokerRemuneration),
			};
		},
		enabled:
			enabled && !!constructionSiteId && !!warrantyType && !!binderAmendmentId,
	});
};

const evaluateEndorsementRenewalPricing = async <T extends OffersWarrantyType>(
	constructionSiteId: string,
	warrantyType: T,
	binderAmendmentId: number,
	body: {
		finalConstructionCost: number;
		applicableGrossRate?: number;
		brokerFees: number;
	},
): Promise<OfferPricingOutput<T, "ENDORSEMENT_RENEWAL">> => {
	const params = new URLSearchParams({
		warrantyType,
		binderAmendmentId: binderAmendmentId.toString(),
		constructionSiteId,
	});

	const { data } = await privateAxios.post(
		`/pricing-files/evaluate-endorsement-renewal-pricing?${params.toString()}`,
		body,
	);

	return data;
};

export const useEvaluateEndorsementRenewalPricing = (
	constructionSiteId: string,
	warrantyType: OffersWarrantyType | null,
	binderAmendmentId: number,
	body: {
		finalConstructionCost: number;
		applicableGrossRate?: number;
		brokerFees: number;
	},
) => {
	return useQuery({
		queryKey: [
			"evaluateEndorsementRenewalPricing",
			constructionSiteId,
			warrantyType,
			binderAmendmentId,
			body.finalConstructionCost,
			body.applicableGrossRate,
		],
		queryFn: async () => {
			if (warrantyType === null) {
				throw new Error("Warranty type is required");
			}

			const pricingData = await evaluateEndorsementRenewalPricing(
				constructionSiteId,
				warrantyType,
				binderAmendmentId,
				body,
			);

			return pricingData;
		},
		enabled: !!constructionSiteId && !!warrantyType && !!binderAmendmentId,
	});
};

const getConstructionSiteUnprocessedCommunicationsCount = async (
	constructionSiteId: string,
) => {
	const { data } = await privateAxios.get<{ count: number }>(
		`/constructionSites/${constructionSiteId}/communications/unprocessed-count`,
	);
	return data.count;
};

export const useGetConstructionSiteUnprocessedCommunicationsCount = (
	constructionSiteId: string,
) => {
	return useQuery({
		queryKey:
			constructionSitesQueryKeys.unprocessedCommunicationsCount(
				constructionSiteId,
			),
		queryFn: () =>
			getConstructionSiteUnprocessedCommunicationsCount(constructionSiteId),
		enabled: !!constructionSiteId,
		refetchInterval: 30000, // Refresh every 30 seconds
	});
};

export const getSubmittableConstructionSiteBindersAndWarranties = async (
	constructionSiteId: string,
) => {
	const { data } = await privateAxios.get<
		AvailableBindersAndWarrantiesForConstructionSite[]
	>(
		`/constructionSites/${constructionSiteId}/submittable-binders-and-warranties`,
	);
	return data;
};

export const useSubmittableConstructionSiteBindersAndWarranties = (
	constructionSiteId: string,
) => {
	return useQuery({
		queryKey:
			constructionSitesQueryKeys.submittableBindersAndWarranties(
				constructionSiteId,
			),
		queryFn: () =>
			getSubmittableConstructionSiteBindersAndWarranties(constructionSiteId),
		enabled: !!constructionSiteId,
	});
};

export const getConstructionSiteTasks = async (
	constructionSiteId: string,
): Promise<Task[]> => {
	const { data } = await privateAxios.get<Task[]>(
		`/constructionSites/${constructionSiteId}/tasks`,
	);
	return data;
};

export const useGetConstructionSiteTasks = (constructionSiteId: string) => {
	return useQuery({
		queryKey: constructionSitesQueryKeys.tasks(constructionSiteId),
		queryFn: () => getConstructionSiteTasks(constructionSiteId),
		enabled: !!constructionSiteId,
	});
};

export const generateConstructionSiteTask = async (
	constructionSiteId: string,
	taskType: TaskType,
) => {
	const { data } = await privateAxios.post(
		`/constructionSites/${constructionSiteId}/generate-task`,
		{ taskType },
	);
	return data;
};

export const useGenerateConstructionSiteTask = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			constructionSiteId,
			taskType,
		}: {
			constructionSiteId: string;
			taskType: TaskType;
		}) => generateConstructionSiteTask(constructionSiteId, taskType),
		onSuccess: (_, { constructionSiteId }) => {
			queryClient.invalidateQueries({
				queryKey: constructionSitesQueryKeys.tasks(constructionSiteId),
			});
		},
	});
};
